import { useSelector } from 'react-redux';
import { useRepositoryQuery } from '@mews-commander/data-access';
import { LocalizedText, lt, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Body, Button, ButtonBaseProps, IconName, Stack, StackItem, Tag, Typography } from '@optimus-web/core';
import { isCurrentEnterprisePortfolio } from '@mews-commander/legacy-entities';
import { StatusTag } from '../components/StatusTag';
import { SecurityCard } from '../components/SecurityCard';
import { ContactUsButton } from '../components/ContactUsButton';
import { useSecurityDashboardFlags } from '../SecurityDashboard/useSecurityDashboardFlags';
import { SecurityDataTestId } from '../utils/SecurityDataTestId';
import { ConfigureSsoPanel } from './ConfigureSsoPanel';
import { SsoDetailsPanel } from './SsoDetails';

// The `isEnabled` prop can be used in any enterprise, it reflects if parent portfolio has SSO enabled.
// `data.isEnabled` fetched from ssoConnection/get will be available only in portfolio.
export function SsoConfiguration({ isEnabled, isRequired }: { isEnabled?: boolean; isRequired?: boolean }) {
    const [ssoConfigPanelProps, toggleConfigSsoPanel] = useDialog();
    const [ssoDetailsPanelProps, toggleDetailsSsoPanel] = useDialog();
    const isPortfolio = useSelector(isCurrentEnterprisePortfolio);

    const { hasSsoSubscription } = useSecurityDashboardFlags();
    const canConfigureSso = isPortfolio && hasSsoSubscription;

    const { data, isLoading } = useRepositoryQuery(
        'ssoConnection',
        'get',
        {},
        {
            enabled: hasSsoSubscription,
        }
    );

    const buttonProps = data?.hasConnection
        ? ({
              variant: 'tertiary',
              onClick: toggleDetailsSsoPanel,
              type: 'submit',
          } satisfies ButtonBaseProps)
        : ({
              variant: 'secondary',
              onClick: toggleConfigSsoPanel,
              type: 'submit',
          } satisfies ButtonBaseProps);

    const getCardVariant = () => {
        if (ssoConfigPanelProps.active || ssoDetailsPanelProps.active) {
            return 'highlighted';
        }

        if (!hasSsoSubscription) {
            return 'subtle';
        }
    };

    return (
        <>
            <SecurityCard
                icon={IconName.Key}
                variant={getCardVariant()}
                isLoading={isLoading}
                data-test-id={SecurityDataTestId.SsoCard}
                action={
                    <>
                        {canConfigureSso && (
                            <Button {...buttonProps} size="small">
                                <LocalizedText textKey={data?.hasConnection ? TextKey.Manage : TextKey.Configure} />
                            </Button>
                        )}

                        {!hasSsoSubscription && <ContactUsButton />}
                    </>
                }
            >
                <Stack spacing="100" itemAlignment="center">
                    <Typography textStyle="titleSmallStrong" as="div">
                        {lt('ConfigureSsoCardTitle')}
                    </Typography>

                    {isEnabled && isRequired ? (
                        <Tag variant="success">
                            <LocalizedText textKey={TextKey.Required} />
                        </Tag>
                    ) : (
                        <StatusTag isEnabled={isEnabled} />
                    )}
                </Stack>

                <StackItem>
                    <Body variant="secondary">{lt('ConfigureSsoCardDescription')}</Body>
                    {!isPortfolio && <Body variant="secondary">{lt('ConfigureSsoCardDescriptionEnterprise')}</Body>}
                </StackItem>
            </SecurityCard>

            {canConfigureSso && (
                <>
                    <ConfigureSsoPanel {...ssoConfigPanelProps} />
                    <SsoDetailsPanel {...ssoDetailsPanelProps} />
                </>
            )}
        </>
    );
}
